import { useEffect } from "react";
import { isNativePlatform } from "../lib/nativeAuth";
import { setKeyboardInset, setKeyboardShown } from "./keyboardInset";

/**
 * Native-only bridge that feeds the reported Android keyboard height into the
 * `keyboardInset` store.
 *
 * Mounted once at the root of the app (App.js) rather than per screen: the IME
 * can open on any screen, and a listener whose lifetime depends on which route
 * happens to be showing would leave a stale inset behind on navigation.
 *
 * `keyboardWillShow` fires at the *start* of the show animation with the final
 * height, so the layout has already reflowed by the time the keyboard finishes
 * sliding up. The matching close uses `keyboardDidHide` — the *end* of the hide
 * animation — deliberately: releasing the inset on `willHide` would open a
 * blank strip under the composer for the duration of the slide-down.
 *
 * A no-op on web: the listeners are never registered, and @capacitor/keyboard
 * is imported lazily so neither the browser bundle nor Jest ever loads the
 * native package. Mirrors useAuthDeepLink (#83).
 */
const useKeyboardBridge = () => {
  useEffect(() => {
    if (!isNativePlatform()) return undefined;

    let cancelled = false;
    let removeListeners = () => {};

    // The plugin reports the keyboard only from inset animations, and nothing
    // promises one when Android drops the keyboard because the app went to the
    // background. A height kept from then would pad the composer and hide the
    // bottom nav (B-04) for a keyboard that is gone. A hidden app shows no
    // keyboard, so let go of it here. A keyboard back on return is reported
    // again if it animates in; if not, the nav shows over it, as before B-04.
    const onHidden = () => {
      if (!document.hidden) return;
      setKeyboardInset(0);
      setKeyboardShown(false);
    };
    document.addEventListener("visibilitychange", onHidden);

    // Only the part of the keyboard the WebView has NOT already given up.
    //
    // Every consumer pads by this inset on the assumption that the WebView
    // keeps its full height under the keyboard. On Android 16 (QA tablet,
    // versionCode 11) the WebView ALSO shrinks by the keyboard, so the chat
    // composer was lifted twice and floated a whole keyboard-height above it,
    // at the phone viewport and at the tablet's native size alike. Measuring
    // what the layout viewport lost — and re-measuring on every resize, since
    // the resize lands after `keyboardWillShow` — is right for both
    // behaviours: no shrink → the full height, as before; full shrink → 0.
    let reported = 0;
    let baseline = window.innerHeight;
    const apply = () => {
      const lost = Math.max(0, baseline - window.innerHeight);
      setKeyboardInset(Math.max(0, reported - lost));
    };
    const onResize = () => {
      if (reported > 0) apply();
      else baseline = window.innerHeight;
    };
    window.addEventListener("resize", onResize);

    (async () => {
      const { Keyboard } = await import("@capacitor/keyboard");
      if (cancelled) return;

      const shown = await Keyboard.addListener("keyboardWillShow", (info) => {
        // Already density-corrected by the plugin, so this is CSS pixels.
        reported = Number(info?.keyboardHeight) || 0;
        setKeyboardShown(true);
        apply();
      });
      const hidden = await Keyboard.addListener("keyboardDidHide", () => {
        reported = 0;
        setKeyboardInset(0);
        setKeyboardShown(false);
      });

      removeListeners = () => {
        shown.remove();
        hidden.remove();
      };
      if (cancelled) removeListeners();
    })();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("resize", onResize);
      removeListeners();
      // Never leave the app padded for a keyboard that no longer has a
      // listener behind it.
      setKeyboardInset(0);
      setKeyboardShown(false);
    };
  }, []);
};

export default useKeyboardBridge;
